import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Users, Hourglass } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

// Enhancer/admin: program groups they host, waitlist demand, templates → open a new cohort.
export const HostedPrograms = () => {
  const qc = useQueryClient();
  const { data: groups = [] } = useQuery({ queryKey: ["hosted-programs"], queryFn: () => api.get("/programs/hosted").then((r) => r.data) });
  const { data: templates = [] } = useQuery({ queryKey: ["program-templates"], queryFn: () => api.get("/programs/templates").then((r) => r.data) });
  const clone = async (t) => {
    try {
      const { data } = await api.post(`/programs/templates/${t.id}/clone`);
      toast.success(`Opened ${data.group.name}${data.waitlist_invited ? ` — ${data.waitlist_invited} waitlisted clients invited` : ""}.`);
      qc.invalidateQueries({ queryKey: ["hosted-programs"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <section className="eyw-card p-6 mt-8" data-testid="hosted-programs">
      <h3 className="font-serif text-xl text-[#1A242B] mb-1 flex items-center gap-2"><Users size={17} className="text-[#D98E73]" /> Program groups</h3>
      <p className="text-xs text-[#8FA89B] mb-4">Each package gets a private group. Buyers join automatically.</p>
      {groups.length === 0 ? <p className="text-sm text-[#8FA89B]" data-testid="hosted-programs-empty">No program groups yet — they appear when someone buys one of your packages.</p> : (
        <ul className="divide-y divide-[#F1EDE6]">
          {groups.map((g) => (
            <li key={g.id} className="py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`hosted-program-${g.id}`}>
              <Link to={`/community/groups/${g.slug}`} className="flex-1 text-[#144F75] hover:underline">{g.name}</Link>
              <span>{g.member_count}{g.capacity ? `/${g.capacity}` : ""} members</span>
              <span className={g.waitlist_count ? "text-[#b8694f] font-medium" : "text-[#8FA89B]"} data-testid={`hosted-program-waitlist-${g.id}`}><Hourglass size={12} className="inline" /> {g.waitlist_count} waiting</span>
            </li>
          ))}
        </ul>
      )}
      {templates.length > 0 && (
        <div className="mt-5">
          <p className="eyw-label mb-2">Templates</p>
          <ul className="space-y-2">
            {templates.map((t) => (
              <li key={t.id} className="flex items-center gap-3 text-sm" data-testid={`program-template-${t.id}`}>
                <span className="flex-1">{t.name}</span>
                <button onClick={() => clone(t)} className="eyw-btn-ghost !py-1.5 text-sm" data-testid={`program-template-clone-${t.id}`}><Copy size={13} /> Open new group</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export const ProgramRoomBookings = () => {
  const { data = [] } = useQuery({ queryKey: ["manager-program-bookings"], queryFn: () => api.get("/manager/me/program-bookings").then((r) => r.data) });
  return (
    <section className="eyw-card p-6 mt-8" data-testid="program-room-bookings">
      <h3 className="font-serif text-xl text-[#1A242B] mb-1">Program groups using your rooms</h3>
      <p className="text-xs text-[#8FA89B] mb-4">Upcoming sessions and events booked by program groups at your center.</p>
      {data.length === 0 ? <p className="text-sm text-[#8FA89B]" data-testid="program-room-bookings-empty">No program bookings coming up.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="text-left text-xs text-[#4A5D6B] border-b border-[#E5E0D8]"><th className="py-2">When</th><th>Group</th><th>Session</th><th>Room</th><th>Enhancer</th></tr></thead>
            <tbody>{data.map((r, i) => (
              <tr key={i} className="border-b border-[#F1EDE6]" data-testid={`program-room-booking-${i}`}>
                <td className="py-2 whitespace-nowrap">{r.date} {r.start_time}</td><td>{r.group}</td><td>{r.service}</td><td>{r.room || "—"}</td><td>{r.enhancer || "—"}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export const ProgramHealth = () => {
  const { data } = useQuery({ queryKey: ["program-health"], queryFn: () => api.get("/admin/programs/health").then((r) => r.data) });
  if (!data) return null;
  const Stat = ({ l, v, t }) => <div className="eyw-card p-4" data-testid={t}><p className="text-xs text-[#8FA89B]">{l}</p><p className="font-serif text-2xl text-[#144F75]">{v}</p></div>;
  return (
    <div className="space-y-6" data-testid="program-health">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat l="Active groups" v={data.active_groups} t="ph-active" /><Stat l="Members" v={data.total_members} t="ph-members" />
        <Stat l="Program revenue" v={`$${data.total_revenue.toFixed(2)}`} t="ph-revenue" /><Stat l="All cohorts" v={data.total_groups} t="ph-total" />
      </div>
      <div className="eyw-card p-5 overflow-x-auto">
        <p className="eyw-label mb-3">Cohorts</p>
        <table className="w-full text-sm min-w-[640px]" data-testid="ph-cohorts">
          <thead><tr className="text-left text-xs text-[#4A5D6B] border-b border-[#E5E0D8]"><th className="py-2">Group</th><th>Host</th><th>Members</th><th>Revenue</th><th>Package redemption</th></tr></thead>
          <tbody>{data.cohorts.map((c) => (
            <tr key={c.id} className="border-b border-[#F1EDE6]"><td className="py-2"><Link to={`/community/groups/${c.slug}`} className="text-[#144F75]">{c.name}</Link></td><td>{c.host}</td>
              <td>{c.members}{c.capacity ? `/${c.capacity}` : ""}</td><td>${c.revenue.toFixed(2)}</td><td>{c.redemption_rate}% ({c.sessions_used}/{c.sessions_total})</td></tr>))}</tbody>
        </table>
      </div>
      <div className="eyw-card p-5">
        <p className="eyw-label mb-3">Most active programs by enhancer</p>
        <ol className="space-y-1 text-sm" data-testid="ph-top-enhancers">{data.top_enhancers.map((e) => <li key={e.name}>{e.name} — {e.members} members · {e.groups} groups · ${e.revenue.toFixed(2)}</li>)}</ol>
      </div>
    </div>
  );
};
